import { Cpu, Pencil, Plus, RotateCcw, Trash2, Users as UsersIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { Team, TeamWithCount } from "@/api/types";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDestructiveDialog } from "@/components/ui/confirm-destructive-dialog";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { ViewToggle, useViewMode } from "@/components/ui/view-toggle";
import { AllowedModelsDialog } from "@/features/teams/components/AllowedModelsDialog";
import { TeamFormDialog } from "@/features/teams/components/TeamFormDialog";
import { TeamMembersDialog } from "@/features/teams/components/TeamMembersDialog";
import {
  useDeactivateTeam,
  useReactivateTeam,
  useTeams,
} from "@/features/teams/hooks/useTeams";
import { getTeamIcon } from "@/lib/team-icons";
import { Action } from "@/permissions/actions";
import { useCan } from "@/permissions/hooks";

export function TeamsListPage() {
  const { t } = useTranslation("teams");
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [membersTeam, setMembersTeam] = useState<Team | null>(null);
  const [allowedTeam, setAllowedTeam] = useState<Team | null>(null);
  const [deactivateTeam, setDeactivateTeam] = useState<Team | null>(null);
  const [view, setView] = useViewMode("teams:view", "cards");

  const { data, isLoading, isError, error, refetch } = useTeams({
    include_inactive: showInactive,
  });
  const deactivate = useDeactivateTeam();
  const reactivate = useReactivateTeam();
  const canCreate = useCan(Action.TEAM_CREATED);

  async function handleDeactivate(team: Team) {
    try {
      await deactivate.mutateAsync(team.id);
      toast.success(t("toasts.deactivated"));
      setDeactivateTeam(null);
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleReactivate(team: Team) {
    try {
      await reactivate.mutateAsync(team.id);
      toast.success(t("toasts.reactivated"));
    } catch (err) {
      toastApiError(err);
    }
  }

  // Cluster de ações reutilizado entre cartões e linhas da lista.
  function renderActions(team: TeamWithCount) {
    return (
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => setMembersTeam(team)}>
          {t("members")}
        </Button>
        {canCreate ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAllowedTeam(team)}
          >
            <Cpu className="h-3 w-3" />
            {t("allowedModels")}
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          disabled={!canCreate}
          onClick={() => {
            setEditingTeam(team);
            setFormOpen(true);
          }}
        >
          <Pencil className="h-3 w-3" />
          {t("edit")}
        </Button>
        {team.is_active ? (
          <Button
            size="sm"
            variant="ghost"
            className="text-danger-700 hover:text-danger-700"
            disabled={!canCreate || deactivate.isPending}
            onClick={() => setDeactivateTeam(team)}
          >
            <Trash2 className="h-3 w-3" />
            {t("deactivate")}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            disabled={!canCreate || reactivate.isPending}
            onClick={() => handleReactivate(team)}
          >
            <RotateCcw className="h-3 w-3" />
            {t("activate")}
          </Button>
        )}
      </div>
    );
  }

  const columns = useMemo<DataTableColumn<TeamWithCount>[]>(
    () => [
      {
        key: "team",
        header: t("columns.team"),
        render: (team) => {
          const TeamIcon = getTeamIcon(team.icon);
          return (
            <div className="flex items-center gap-2">
              <span
                className="flex h-8 w-8 items-center justify-center rounded-lg"
                style={{
                  backgroundColor: team.color
                    ? `${team.color}1A`
                    : "var(--color-surface-muted)",
                  color: team.color ?? "var(--color-ink-950)",
                }}
                aria-hidden
              >
                <TeamIcon className="h-4 w-4" />
              </span>
              <div className="flex items-center gap-2">
                <span className="font-medium text-ink-950">{team.name}</span>
                {!team.is_active ? (
                  <Badge variant="destructive">{t("inactive")}</Badge>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        key: "managers",
        header: t("columns.managers"),
        render: (team) => team.manager_count,
      },
      {
        key: "members",
        header: t("columns.members"),
        render: (team) => team.member_count,
      },
      {
        key: "models",
        header: t("columns.models"),
        render: (team) => team.allowed_models_count,
      },
      {
        key: "actions",
        header: t("columns.actions"),
        render: (team) => renderActions(team),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, canCreate, deactivate.isPending, reactivate.isPending],
  );

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("title")}
        description={t("description")}
        actions={
          <>
            <Button
              variant={showInactive ? "secondary" : "outline"}
              onClick={() => setShowInactive((v) => !v)}
            >
              {showInactive ? t("hideInactive") : t("showInactive")}
            </Button>
            <ViewToggle
              value={view}
              onChange={setView}
              labels={{ cards: t("viewCards"), list: t("viewList") }}
            />
            <Button
              onClick={() => {
                setEditingTeam(null);
                setFormOpen(true);
              }}
              disabled={!canCreate}
            >
              <Plus className="h-4 w-4" />
              {t("create")}
            </Button>
          </>
        }
      />

      {data && data.length === 0 ? (
        <EmptyState
          title={t("empty.title")}
          description={t("empty.description")}
        />
      ) : view === "list" ? (
        <DataTable
          columns={columns}
          rows={data ?? []}
          getRowKey={(team) => team.id}
        />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data?.map((team) => {
            const TeamIcon = getTeamIcon(team.icon);
            return (
              <li
                key={team.id}
                className="shadow-card flex flex-col rounded-2xl border border-line bg-surface p-5 transition-all hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-hover"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span
                      className="flex h-10 w-10 items-center justify-center rounded-xl"
                      style={{
                        backgroundColor: team.color
                          ? `${team.color}1A`
                          : "var(--color-surface-muted)",
                        color: team.color ?? "var(--color-ink-950)",
                      }}
                      aria-hidden
                    >
                      <TeamIcon className="h-5 w-5" />
                    </span>
                    <div>
                      <h2 className="text-base font-semibold text-ink-950">
                        {team.name}
                      </h2>
                      <p className="text-xs text-ink-500">{team.slug}</p>
                    </div>
                  </div>
                  {!team.is_active ? (
                    <Badge variant="destructive">{t("inactive")}</Badge>
                  ) : null}
                </div>
                <p className="mt-2 line-clamp-2 text-sm text-ink-500">
                  {team.description ?? t("noDescription")}
                </p>
                <div className="mt-3 flex items-center gap-2 text-xs text-ink-500">
                  <UsersIcon className="h-3 w-3" />
                  {t("membersCount", { count: team.member_count })}
                </div>
                <div className="mt-3">{renderActions(team)}</div>
              </li>
            );
          })}
        </ul>
      )}

      <TeamFormDialog
        open={formOpen}
        onOpenChange={(v) => {
          setFormOpen(v);
          if (!v) setEditingTeam(null);
        }}
        team={editingTeam}
      />
      {membersTeam ? (
        <TeamMembersDialog
          open={Boolean(membersTeam)}
          onOpenChange={(v) => {
            if (!v) setMembersTeam(null);
          }}
          teamId={membersTeam.id}
          teamName={membersTeam.name}
        />
      ) : null}
      {allowedTeam ? (
        <AllowedModelsDialog
          open={Boolean(allowedTeam)}
          onOpenChange={(v) => {
            if (!v) setAllowedTeam(null);
          }}
          teamId={allowedTeam.id}
          teamName={allowedTeam.name}
        />
      ) : null}

      {deactivateTeam ? (
        <ConfirmDestructiveDialog
          open={Boolean(deactivateTeam)}
          onOpenChange={(v) => {
            if (!v) setDeactivateTeam(null);
          }}
          title={t("confirmDeactivate.title")}
          description={t("confirmDeactivate.description")}
          resourceName={deactivateTeam.name}
          resourceKind={t("confirmDeactivate.resourceKind")}
          confirmLabel={t("confirmDeactivate.confirm")}
          onConfirm={() => handleDeactivate(deactivateTeam)}
          isPending={deactivate.isPending}
        />
      ) : null}
    </div>
  );
}
